<template>
  <base-modal :visible="visible" :width="'620px'" @close="close">
    <template #title>
      <i class="fas fa-map-marker-alt"></i>
      <span>{{ title }}</span>
    </template>

    <!-- Список существующих точек -->
    <div class="mpp">
      <div class="mpp__label">Действующие точки</div>
      <div v-if="options.length" class="mpp__options">
        <button
          v-for="(opt, idx) in options"
          :key="optId(opt) || `o-${idx}`"
          type="button"
          class="mpp__option"
          :class="{ 'mpp__option--active': optName(opt) === selectedName }"
          @click="useExisting(opt)"
        >
          <i class="fas fa-map-pin"></i> {{ optName(opt) }}
        </button>
      </div>
      <div v-else class="mpp__empty u-text-tertiary">Пока нет действующих точек</div>

      <div class="mpp__divider">
        <span>или добавьте новую на карте</span>
      </div>

      <!-- Карта -->
      <div class="mpp__map-wrap">
        <div ref="map" class="mpp__map"></div>

        <!-- Плавающий тултип при выборе на карте -->
        <div v-if="pendingName !== null" class="mpp__pending">
          <input
            v-model="pendingName"
            class="mpp__pending-input"
            placeholder="Название точки"
          />
          <div class="mpp__pending-actions">
            <app-button size="sm" icon="fas fa-check" @click="useNew">Использовать</app-button>
            <app-button size="sm" variant="ghost" icon="fas fa-times" @click="cancelNew">Отмена</app-button>
          </div>
        </div>

        <div class="mpp__hint"><i class="fas fa-mouse-pointer"></i> Кликните на карту, чтобы поставить точку</div>
      </div>
    </div>
  </base-modal>
</template>

<script>
/* global ymaps */
export default {
  name: 'MapPointPicker',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: 'Выбор точки' },
    value: { type: String, default: '', },
    options: { type: Array, default: () => [] }
  },
  data() {
    return {
      selectedName: null,       // название текущей выбранной точки (в модалке)
      pendingName: null,        // название/адрес новой точки с карты (для отображения)
      pendingCoords: null,      // [lat, lon] новой точки
      pendingAddress: null,     // полный Яндекс GeoObject-метаданные адреса (для points.data)
      geomap: null,             // экземпляр карты
      geometryPlacemark: null   // метка
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.selectedName = this.value || null
        this.pendingName = null
        this.pendingCoords = null
        this.pendingAddress = null
        // Ждём, пока transition в BaseModal вставит контент в DOM
        this.$nextTick(() => {
          setTimeout(() => this.initMap(), 120)
        })
      } else {
        this.destroyMap()
      }
    }
  },
  beforeDestroy() {
    this.destroyMap()
  },
  methods: {
    initMap() {
      const container = this.$refs.map
      if (!container) return
      // Уже инициализирована
      if (this.geomap) return

      ymaps.ready(() => {
        if (!container.isConnected) return

        this.geomap = new ymaps.Map(container, {
          center: [57.927444, 59.997235],
          zoom: 9,
          controls: ['zoomControl', 'fullscreenControl']
        })

        // Клик по карте — добавить новую точку
        this.geomap.events.add('click', (e) => {
          const coords = e.get('coords')
          this.startNewFromMap(coords)
        })
      })
    },
    destroyMap() {
      if (this.geomap) {
        this.geomap.destroy()
        this.geomap = null
      }
      this.geometryPlacemark = null
    },
    // Установить новую метку на карте
    setMarker(coords, content) {
      if (!this.geomap) return
      if (this.geometryPlacemark) {
        this.geomap.geoObjects.remove(this.geometryPlacemark)
      }
      this.geometryPlacemark = new ymaps.Placemark(coords, {
        hintContent: content || 'Новая точка',
        balloonContent: content || 'Новая точка'
      }, {
        preset: 'islands#blueCircleDotIcon',
        iconColor: '#3b82f6'
      })
      this.geomap.geoObjects.add(this.geometryPlacemark)
      this.geomap.panTo(coords, { duration: 300, flying: true })
    },
    // Элемент может быть строкой или объектом { id, name }
    optName(opt) {
      if (!opt) return ''
      return typeof opt === 'object' ? (opt.name || '') : opt
    },
    optId(opt) {
      if (!opt) return null
      return typeof opt === 'object' ? (opt.id !== undefined ? opt.id : null) : null
    },
    coordsOf(opt) {
      if (!opt) return null
      if (typeof opt === 'object' && opt.coords) return opt.coords
      return null
    },
    // Метаданные адреса, если есть в объекте точки (например из справочника /points)
    addrOf(opt) {
      if (!opt) return null
      if (typeof opt === 'object') return (opt.address !== undefined) ? opt.address : (opt.data || null)
      return null
    },
    // Выбрать существующую точку из списка — эмитим сразу
    useExisting(opt) {
      const name = this.optName(opt)
      this.selectedName = name
      this.$emit('select', {
        id: this.optId(opt),
        name: name,
        coords: this.coordsOf(opt),
        address: this.addrOf(opt),
        custom: false
      })
      this.close()
    },
    // Начать добавление новой точки (кликом по карте)
    startNewFromMap(coords) {
      // Геокодируем адрес для предзаполнения названия
      this.setMarker(coords)
      this.pendingCoords = coords
      this.pendingName = null // пустое — юзер сам впишет или подставим адрес
      this.pendingAddress = null

      ymaps.geocode(coords).then((res) => {
        const obj = res.geoObjects.get(0)
        if (obj) {
          // Название — как адрес геокодера
          this.pendingName = obj.getAddressLine()

          // Полный Яндекс GeoObject для points.data: берём metaDataProperty
          // (там Address.formatted + Address.Components с городом/улицей/домом),
          // в крайнем случае — полный объект toJSON().
          let meta = null
          try {
            meta = obj.properties ? obj.properties.get('metaDataProperty') : null
          } catch (e) { meta = null }
          if (!meta) {
            try { meta = (obj.toJSON && obj.toJSON()) || null } catch (e) { meta = null }
          }
          this.pendingAddress = meta
        }
      }).catch(() => {
        this.pendingName = null
        this.pendingAddress = null
      })
    },
    // Использовать новую (с карты) точку
    useNew() {
      const name = (this.pendingName || '').trim()
      if (!name) {
        alert('Введите название точки')
        return
      }
      this.selectedName = name
      // Передаём имя + координаты + полный адрес (новая точка, без id)
      this.$emit('select', {
        id: null,
        name: name,
        coords: this.pendingCoords,
        address: this.pendingAddress,
        custom: true
      })
      this.close()
    },
    cancelNew() {
      if (this.geomap && this.geometryPlacemark) {
        this.geomap.geoObjects.remove(this.geometryPlacemark)
      }
      this.geometryPlacemark = null
      this.pendingName = null
      this.pendingCoords = null
      this.pendingAddress = null
    },
    close() {
      this.$emit('close')
    }
  },
  mounted() {
    // Карта инициализируется при открытии (watch.visible → initMap).
    // Ничего делать при монтировании не нужно.
  }
}
</script>

<style scoped>
.mpp {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
}

.mpp__label {
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}

.mpp__options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
}

.mpp__option {
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  background: var(--bg-surface-muted);
  color: var(--text-primary);
  border-radius: var(--radius-pill);
  padding: var(--space-2) var(--space-8);
  font-size: var(--text-sm);
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  transition: background var(--duration-fast) var(--ease-base),
              border-color var(--duration-fast) var(--ease-base);
}
.mpp__option:hover { background: var(--bg-subtle); }
.mpp__option--active {
  background: var(--bg-selected);
  border-color: var(--border-selected);
  font-weight: var(--font-weight-medium);
}
.mpp__option i { color: var(--primary); font-size: var(--text-xs); }

.mpp__empty {
  font-size: var(--text-sm);
  font-style: italic;
}

.mpp__divider {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  color: var(--text-tertiary);
  font-size: var(--text-xs);
}
.mpp__divider::before,
.mpp__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-default);
}

.mpp__map-wrap {
  position: relative;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
}

.mpp__map {
  width: 100%;
  height: 320px;
  display: block;
}

.mpp__hint {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  color: var(--text-secondary);
  padding: var(--space-2) var(--space-8);
  border-radius: var(--radius-pill);
  font-size: var(--text-2xs);
  box-shadow: var(--shadow-sm);
  white-space: nowrap;
  pointer-events: none;
}

.mpp__pending {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  background: var(--bg-surface);
  border-radius: var(--radius-lg);
  padding: var(--space-8);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  z-index: 10;
}

.mpp__pending-input {
  width: 100%;
  padding: var(--space-4) var(--space-6);
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-family: inherit;
}
.mpp__pending-input:focus {
  outline: none;
  border-color: var(--border-focus);
}

.mpp__pending-actions {
  display: flex;
  gap: var(--space-6);
}
</style>
